import { ViewType, ViewObject } from '../base';

import { PriceType, PriceInfo } from '@unpispas/upp-defs';
import { dataService } from '../../data';
import { TicketDiscount } from '../objects/ticketdiscount';

import { TicketView } from './ticket';
import { DiscountView } from './discount';

export interface TicketDiscountInfo {
    charge: number,         // total amount to be charged for the ticket discount
    refund: number          // total amount to be refunded for the ticket discount
}

/****************************/
/* CLASS VIEW               */
/****************************/

export class _TicketDiscountView extends ViewObject<TicketDiscount> {

    /**********************************/
    /* VIEWOBJECT IMPLEMENTATION      */
    /**********************************/

    private get ticketdiscount(): TicketDiscount{
        return this.object;
    }
             
    constructor(object: TicketDiscount, data: dataService){
        super(object, data);
    }

    override OnDestroy(): void {
        super.OnDestroy();
    }

    /**********************************/
    /* VIEW MANAGED INFORMATION        */
    /**********************************/

    private _calcdiscountinfo: TicketDiscountInfo | null = null;
    private _calcpriceinfo: PriceType | null = null;    // final price for discount (charge - refund)
    private _calcchargeinfo: PriceType | null = null;   // charge price for discount    
    private _calcrefundinfo: PriceType | null = null;   // refund price for discount

    private RefreshView(targets: string[]){
        if (targets.includes('TICKETDISCOUNT')){
            this.DoRefreshView();
        }

        if (targets.includes('TICKET')){
            this._calcdiscountinfo = null;
            this._calcpriceinfo = null;
            this._calcchargeinfo = null;
            this._calcrefundinfo = null;
            this.DoRefreshView();
        }

        if (targets.includes('DISCOUNT')){
            this._calcdiscountinfo = null;
            this._calcpriceinfo = null;
            this._calcchargeinfo = null;
            this._calcrefundinfo = null;
            this.DoRefreshView();
        }

        if (targets.includes('TICKETPRODUCT')){
            this._calcdiscountinfo = null;
            this._calcpriceinfo = null;
            this._calcchargeinfo = null;
            this._calcrefundinfo = null;
            this.DoRefreshView();
        }
    }

    /**********************************/
    /* CHILDREN REGISTRATION          */
    /**********************************/

    override doRegister(): void{
        const weakref = new WeakRef(this);

        // deregister of current subscriptions
        for (const _subsription of this._subscriptions){
            _subsription.unsubscribe();
        }
        this._subscriptions = [];
        
        // register to updates on contained items         
        this._subscriptions.push(
            this.ticketdiscount.OnRefresh.subscribe(
            (targets: string[]) => {
                const self = weakref.deref();
                if (self){  // do not keep strong references in subscription
                    self.RefreshView(targets);
                }                
            })
        );    

        if (this.ticket){
            this._subscriptions.push(
                this.ticket.OnRefresh.subscribe(
                (targets: string[]) => {
                    const self = weakref.deref();
                    if (self){  // do not keep strong references in subscription
                        self.RefreshView(targets);
                    }                
                })    
            );
        }
    }

    /**********************************/
    /* ACTIVE CHILDREN INFORMATION    */
    /**********************************/   

    get ticket(): TicketView | null {
        return this.alives.get(this.ticketdiscount.ticket) as TicketView;
    }
    
    get discount(): DiscountView | null {
        return this.alives.get(this.ticketdiscount.discount) as DiscountView;
    }

    /**********************************/
    /* CUSTOM METHODS                 */
    /**********************************/  

    get priceinfo(): PriceType {
        if (this._calcpriceinfo === null){
            this._calcpriceinfo = new PriceInfo(this.calcinfo.charge - this.calcinfo.refund).price;       
        }

        return this._calcpriceinfo;
    }

    get chargeinfo(): PriceType {
        if (this._calcchargeinfo === null){
            this._calcchargeinfo = new PriceInfo(this.calcinfo.charge).price;       
        }

        return this._calcchargeinfo;
    }

    get refundinfo(): PriceType {
        if (this._calcrefundinfo === null){
            this._calcrefundinfo = new PriceInfo(this.calcinfo.refund).price;       
        }

        return this._calcrefundinfo;
    }

    get calcinfo(): TicketDiscountInfo {   
        if (this._calcdiscountinfo === null){
            let _charge = 0;
            let _refund = 0;
    
            if (this.ticket && this.discount){
                _charge = this.discount.ChargeAmount(this.ticket);
                _refund = this.discount.RefundAmount(this.ticket);
            }
    
            this._calcdiscountinfo = {
                charge: _charge,
                refund: _refund
            };
        }

        return this._calcdiscountinfo;
    }

    /**
     * Stores the calculated charge on the applied discount.
     * Reserved for the ticket recalculation flow: reading `calcinfo` never
     * leaves the discount pending commit.
     */
    Recalculate(): void {
        const _calcinfo = this.calcinfo;
        this.ticketdiscount.charge = _calcinfo.charge - _calcinfo.refund;
        this._calcdiscountinfo = _calcinfo;      // the write above invalidates the cache
    }
}

/****************************/
/* PROXY VIEW               */
/****************************/

export type TicketDiscountView = ViewType <_TicketDiscountView, TicketDiscount>;

export function TicketDiscountViewProxy(object: TicketDiscount, data: dataService): TicketDiscountView {
    return new _TicketDiscountView(object, data).Proxy as TicketDiscountView;
}